import React, { SyntheticEvent, useEffect, useMemo } from "react";

import { Icon } from "semantic-ui-react";

import { ComponentPropsGetterR } from "react-table-6";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

import CardEmployeeListUX from "./CardEmployeeListUX";

import {
  ACTIONS,
  BTN_ACTS,
  EMPLOYEE_LIMIT,
  MasterData,
  MasterOptions,
  PickedState,
  RunSequence,
  SetProp,
  State,
} from "./sequence/EmployeeList";

// Types
type CardEmployeeListProps = Pick<PickedState, "buttonLoadCheck"> & {
  setProp: SetProp;
  runSequence: RunSequence;
  EmployeeListSequence: State["EmployeeListSequence"];
  languageUX?: string;
  masterData: MasterData;
  masterOptions: MasterOptions;
  onAddEmployee: () => void;
  onSelectEmployee: (data: any) => void;
};

const CardEmployeeList = (props: CardEmployeeListProps) => {
  useEffect(() => {
    props.runSequence({ sequence: "EmployeeList", restart: true });
  }, []);

  const formattedItems = useMemo(
    () =>
      (props.EmployeeListSequence?.pagination?.items ?? []).map((item) => {
        const username = (item.user_employee ?? []).map((acc) => acc.username).join(", ");
        const foundPosition = props.masterOptions.positionAll?.find(
          (option) => Number(option.value) === item.position
        );
        const foundDivision = props.masterOptions.division?.find(
          (option) => Number(option.value) === item.affiliate
        );
        const foundNationality = props.masterData.nationality?.find(
          (acc) => acc.id === item.nationality
        );
        const foundPrenameTh = props.masterOptions.prenameTh?.find(
          (option) => Number(option.value) === item.pre_name
        );
        const foundPrenameEn = props.masterOptions.prenameEn?.find(
          (option) => Number(option.value) === item.pre_name_en
        );
        const firstName =
          props.languageUX === "th"
            ? item.first_name || item.first_name_en
            : item.first_name_en || item.first_name;
        const lastName =
          props.languageUX === "th"
            ? item.last_name || item.last_name_en
            : item.last_name_en || item.last_name;

        const prename =
          foundNationality?.country_code === "TH" || !item.nationality
            ? foundPrenameTh?.text || foundPrenameEn?.text
            : foundPrenameEn?.text;

        return {
          ...item,
          affiliation_name: foundDivision?.text || "",
          employee_code: (
            <div style={{ display: "grid", gridTemplateColumns: "20px 1fr" }}>
              {item.is_info_complete ? (
                <label />
              ) : (
                <Icon style={{ fontSize: "0.925rem" }} color="yellow" name="hourglass half" />
              )}
              <label>{item.code}</label>
            </div>
          ),
          first_name: firstName,
          last_name: lastName,
          position_name: foundPosition?.text || "",
          prename: prename || "",
          status_name: (
            <div style={{ textTransform: "capitalize" }}>{item.status?.toLowerCase()}</div>
          ),
          username,
        };
      }),
    [
      props.EmployeeListSequence?.pagination,
      props.languageUX,
      props.masterData.nationality,
      props.masterOptions.division,
      props.masterOptions.positionAll,
      props.masterOptions.prenameEn,
      props.masterOptions.prenameTh,
    ]
  );

  const handleSearch = () => {
    props.runSequence({
      sequence: "EmployeeList",
      action: ACTIONS.SEARCH,
      activePage: props.EmployeeListSequence?.pagination?.activePage,
    });
  };

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;
    const value = data.type === "checkbox" ? data.checked : data.value;

    props.setProp("EmployeeListSequence.filterEmployee", {
      ...props.EmployeeListSequence?.filterEmployee,
      [name]: value,
    });
  };

  const handleGetTrProps: ComponentPropsGetterR = (state, rowInfo) => {
    const isInfoComplete = rowInfo?.original?.is_info_complete;
    const status = rowInfo?.original?.status;

    let color = "";

    if (status === "INACTIVE") {
      color = "#b3b3b3";
    } else if (!isInfoComplete) {
      color = "#1a73e8";
    }

    return {
      style: { color },
      onClick: () => {
        const items = props.EmployeeListSequence?.pagination?.items ?? [];
        const foundItem = items.find((item) => item.id === rowInfo?.original?.id);

        if (foundItem) {
          props.onSelectEmployee(foundItem);
        }
      },
    };
  };

  const handlePageChange = (activePage: number) => {
    props.runSequence({ sequence: "EmployeeList", action: ACTIONS.SEARCH, activePage });
  };

  console.log("CardEmployeeList props:", props);

  return (
    <div style={{ marginTop: "-1rem", padding: "0 10px" }}>
      <CardEmployeeListUX
        filterEmployee={props.EmployeeListSequence?.filterEmployee}
        tableData={formattedItems}
        affiliateOptions={props.masterOptions.division}
        positionOptions={props.masterOptions.positionAll}
        getTrProps={handleGetTrProps}
        onAddEmployee={props.onAddEmployee}
        onChangeFilter={handleChangeFilter}
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ margin: 0 }}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            size="small"
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        pagination={
          <PaginationShorthand
            activePage={props.EmployeeListSequence?.pagination?.activePage ?? 0}
            limit={EMPLOYEE_LIMIT}
            total={props.EmployeeListSequence?.pagination?.total}
            onPageChange={handlePageChange}
          />
        }
      />
    </div>
  );
};

CardEmployeeList.displayName = "CardEmployeeList";

export default React.memo(CardEmployeeList);
